import { Button } from 'primereact/button';
import { InputText } from "primereact/inputtext";
import { classNames } from 'primereact/utils';
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { MobXProviderContext } from "mobx-react";
import { Toast } from "primereact/toast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { RouterAddress } from "../../../constants/RouterAddress";
import AuthLayout from "../components/AuthLayout";
import { appFormSectionStyle } from "../../../components/general/uiSurfaceStyles";
import { redirectToHomeForAuthMode } from "../../../utils/stringUtils";
import { useAuthExperience } from "../hooks/useAuthExperience";
import { getCognitoBrandClientMetadata } from "../config/brandOverride";

const PENDING_REGISTRATION_STORAGE_KEY = "auth.pendingRegistration";
const PENDING_REGISTRATION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const AUTO_LOGIN_PROFILE_RETRY_COUNT = 12;
const AUTO_LOGIN_PROFILE_RETRY_DELAY_MS = 1000;

type PendingRegistration = {
  email: string;
  username: string;
  password: string;
  createdAt: string;
};

const ConfirmRegistration = () => {
  const { authStore, cognitoHelper } = useContext(MobXProviderContext);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [reloadLogin, setReloadLogin] = useState<boolean>(false);
  const [autoFlowAttempted, setAutoFlowAttempted] = useState<boolean>(false);
  const authExperience = useAuthExperience();
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const routeStateEmail = location?.state?.email || "";
  const routeStateUsername = location?.state?.username || "";
  const routeStatePassword = location?.state?.password || "";

  const queryEmail = String(searchParams.get("email") || "").trim().toLowerCase();
  const queryUsername = String(searchParams.get("username") || "").trim().toLowerCase();
  const queryCode = String(searchParams.get("code") || "").trim();

  const resolvedEmail = queryEmail || routeStateEmail || "";
  const resolvedUsername = queryUsername || routeStateUsername || "";

  const defaultValues = {
    verificationCode: queryCode,
    email: resolvedEmail,
    username: resolvedUsername,
    password: routeStatePassword,
  };
  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  const isAutoConfirmRequest = useMemo(() => Boolean(queryUsername && queryCode), [queryCode, queryUsername]);

  const isValidEmail = (value: string) =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(String(value || "").trim());

  const getPendingRegistration = (): PendingRegistration | null => {
    try {
      const rawPendingRegistration = localStorage.getItem(PENDING_REGISTRATION_STORAGE_KEY);
      if (!rawPendingRegistration) {
        return null;
      }

      const parsedPendingRegistration = JSON.parse(rawPendingRegistration) as PendingRegistration;
      const createdAt = new Date(parsedPendingRegistration?.createdAt || "").getTime();
      if (!createdAt || (Date.now() - createdAt) > PENDING_REGISTRATION_MAX_AGE_MS) {
        localStorage.removeItem(PENDING_REGISTRATION_STORAGE_KEY);
        return null;
      }

      return parsedPendingRegistration;
    } catch (error) {
      localStorage.removeItem(PENDING_REGISTRATION_STORAGE_KEY);
      return null;
    }
  };

  const clearPendingRegistration = () => {
    localStorage.removeItem(PENDING_REGISTRATION_STORAGE_KEY);
  };

  const sleep = (delayMs: number) => new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });

  const waitForUserProfileBootstrap = async () => {
    for (let attempt = 0; attempt < AUTO_LOGIN_PROFILE_RETRY_COUNT; attempt += 1) {
      try {
        await backendAPIStore.init();
        const currentUserId = String(authStore.getUserId() || "").trim();
        if (!currentUserId) {
          await sleep(AUTO_LOGIN_PROFILE_RETRY_DELAY_MS);
          continue;
        }

        const userProfile = await backendAPIStore.getUserProfile(currentUserId);
        if (userProfile) {
          return true;
        }
      } catch (error: any) {
        const message = String(error?.message || "").toLowerCase();
        const shouldRetry = !message || message.includes("not found") || message.includes("profile");
        if (!shouldRetry) {
          throw error;
        }
      }

      await sleep(AUTO_LOGIN_PROFILE_RETRY_DELAY_MS);
    }

    return false;
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>;
  };

  const redirectToLogin = (email: string, username: string, password?: string) => {
    navigate(RouterAddress.LOGIN_PAGE, {
      state: {
        email: String(email || "").toLowerCase(),
        username: String(username || "").toLowerCase(),
        password: password || "",
      }
    });
  };

  const tryAutoLogin = async (email: string, username: string) => {
    const pendingRegistration = getPendingRegistration();
    if (!pendingRegistration) {
      redirectToLogin(email, username);
      return false;
    }

    const normalizedEmail = String(email || "").trim().toLowerCase();
    const normalizedUsername = String(username || "").trim().toLowerCase();
    const pendingEmail = String(pendingRegistration.email || "").trim().toLowerCase();
    const pendingUsername = String(pendingRegistration.username || "").trim().toLowerCase();
    const matchesUser =
      (normalizedEmail && normalizedEmail === pendingEmail)
      || (normalizedUsername && normalizedUsername === pendingUsername);

    if (!matchesUser || !pendingRegistration.password) {
      redirectToLogin(email, username);
      return false;
    }

    const resolvedLoginUsername = pendingEmail || pendingUsername;

    return await new Promise<boolean>((resolve) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: resolvedLoginUsername,
        Password: pendingRegistration.password,
        ClientMetadata: getCognitoBrandClientMetadata(),
      });

      const user = new CognitoUser({
        Username: resolvedLoginUsername,
        Pool: authStore.userPoolProps,
      });
      user.setAuthenticationFlowType("USER_PASSWORD_AUTH");
      user.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const finishAutoLogin = async () => {
            try {
              authStore.setUserProperties(session, user.getUsername());
              await authStore.loadUserContext(true).catch(() => null);
              await waitForUserProfileBootstrap();
              clearPendingRegistration();
              navigate(redirectToHomeForAuthMode(authStore.getAuthMode(), authStore.getUserRoleType()));
              window.location.reload();
              resolve(true);
            } catch {
              clearPendingRegistration();
              redirectToLogin(email, username, "");
              resolve(false);
            }
          };

          void finishAutoLogin();
        },
        onFailure: () => {
          clearPendingRegistration();
          redirectToLogin(email, username, "");
          resolve(false);
        },
        newPasswordRequired: () => {
          clearPendingRegistration();
          redirectToLogin(email, username, "");
          resolve(false);
        },
        customChallenge: () => {
          clearPendingRegistration();
          redirectToLogin(email, username, "");
          resolve(false);
        },
        mfaRequired: () => {
          clearPendingRegistration();
          redirectToLogin(email, username, "");
          resolve(false);
        },
      });
    });
  };

  const completeConfirmationFlow = async (email: string, username: string) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Registration confirmed successfully.',
      detail: '',
      life: 2500,
    });

    await tryAutoLogin(email, username);
  };

  const confirmRegistration = async (email: string, username: string, verificationCode: string, password?: string) => {
    if (!isValidEmail(email)) {
      setErrorMessage("A valid email is required to confirm registration. Please go back and register again.");
      return;
    }

    if (!username) {
      setErrorMessage("Your registration details are missing. Please go back and register again.");
      return;
    }

    setErrorMessage("");
    setLoading(true);
    try {
      await cognitoHelper.confirmRegistration(username, verificationCode);
      reset({
        verificationCode: "",
        email,
        username,
        password: password || "",
      });
      await completeConfirmationFlow(email, username);
    } catch (error: any) {
      const message = String(error?.message || "");
      if (message.includes("Current status is CONFIRMED")) {
        await completeConfirmationFlow(email, username);
        return;
      }
      setErrorMessage(message || "Unable to confirm your registration.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    await confirmRegistration(data.email, data.username, data.verificationCode, data.password);
  };

  const resendCode = async () => {
    if (!isValidEmail(resolvedEmail)) {
      setErrorMessage("A valid email is required to resend the confirmation link. Please register again.");
      return;
    }

    if (!resolvedUsername) {
      setErrorMessage("Your registration details are missing. Please go back and register again.");
      return;
    }

    setErrorMessage("");
    setReloadLogin(true);
    try {
      await cognitoHelper.resendConfirmationCode(resolvedUsername);
      toast.current?.show({ severity: 'success', summary: 'Confirmation link resent successfully.', detail: '', life: 3000 });
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setReloadLogin(false);
    }
  };

  useEffect(() => {
    if (!isAutoConfirmRequest || autoFlowAttempted) {
      return;
    }

    setAutoFlowAttempted(true);
    void confirmRegistration(resolvedEmail, resolvedUsername, queryCode, routeStatePassword);
  }, [autoFlowAttempted, isAutoConfirmRequest, queryCode, resolvedEmail, resolvedUsername, routeStatePassword]);

  return (
    <AuthLayout
      title="Confirm Registration"
      subtitle={isAutoConfirmRequest
        ? authExperience.auth.autoConfirmSubtitle
        : authExperience.auth.confirmRegistrationSubtitle}
    >
      <Toast ref={toast} />
      <form onSubmit={handleSubmit(onSubmit)} className="justify-content-center align-items-center flex-column flex" style={appFormSectionStyle}>
        {loading && isAutoConfirmRequest ? (
          <div className="text-center py-4">
            <h4 style={{ marginBottom: "0.75rem" }}>Confirming your account...</h4>
            <p style={{ margin: 0, opacity: 0.8 }}>Please wait while we activate your account and attempt to sign you in.</p>
          </div>
        ) : (
          <>
            <div className="field mt-5">
              <span className="p-float-label">
                <Controller name="verificationCode" control={control} rules={{ required: 'Verification code is required.' }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
                <label htmlFor="verificationCode" className={classNames({ 'p-error': errors.verificationCode })}>Verification Code</label>
              </span>
              {getFormErrorMessage('verificationCode')}
            </div>

            <div className="grid">
              <div className="col" style={{ textAlign: "center" }}>
                <Button type="submit" label="Confirm Account" severity="warning" rounded className="p-button-outlined" loading={loading} />
              </div>
              <div className="col" style={{ textAlign: "center" }}>
                <a onClick={resendCode} style={{ marginLeft: "1em", cursor: "pointer", marginTop: "1em", opacity: reloadLogin ? 0.6 : 1 }}>
                  {reloadLogin ? "Resending..." : "Resend Link"}
                </a>
              </div>
            </div>
          </>
        )}

        {!loading && errorMessage ? <div className="p-error mt-3">{errorMessage}</div> : null}

        <div className="grid" style={{ minWidth: "70%", alignItems: "center", textAlign: "center", marginTop: "1em" }}>
          <p className="col">
            Back to - &nbsp;
            <a onClick={() => navigate(RouterAddress.LOGIN_PAGE)}>
              Login
            </a>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ConfirmRegistration;
